import { AlertCircle } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import { ApiError, Congress } from "@/client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useCongress } from "@/features/congress-select/hooks/use-congress";
import { queryClient } from "@/libs/react-query";
import { cn } from "@/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";

import { congressMetaQueryKey } from "../api/get-congress";
import { useUpdateCongress } from "../api/update-congress";
import {
  SectionDetailsFormData,
  sectionDetailsSchema,
} from "../types/section-details-schema";

import MacrosPopover from "./macros-popover";

type SectionDetailsFormProps = {
  onSuccess?: () => void;
  initialData?: Congress;
};

function transformApiToFormData(congress?: Congress): SectionDetailsFormData {
  return {
    sectionLeftTemplate: congress?.sectionLeftTemplate ?? "",
    sectionRightTemplate: congress?.sectionRightTemplate ?? "",
    isSectionRatingEnabled: congress?.isSectionRatingEnabled ?? false,
    isSectionQaEnabled: congress?.isSectionQaEnabled ?? false,
    displayPosterDay: congress?.displayPosterDay ?? false,
    displaySectionGroup: congress?.displaySectionGroup ?? false,
  };
}

const macros = [
  { name: "Short ID", code: "short_name" },
  { name: "Title", code: "name" },
  { name: "Secondary Title", code: "name2" },
  { name: "Additional Secondary Title", code: "name3" },
  { name: "Start Date", code: "date" },
  { name: "End Date", code: "date_end" },
  { name: "Start Time", code: "start_time" },
  { name: "End Time", code: "end_time" },
  { name: "Room Name", code: "room" },
  { name: "Moderator(s)", code: "chair_free" },
  { name: "Track Name", code: "track" },
  { name: "Group Name", code: "section_group" },
];
export function SectionDetailsForm(props: SectionDetailsFormProps) {
  const { t } = useTranslation();
  const { initialData } = props;
  const form = useForm<SectionDetailsFormData>({
    resolver: zodResolver(sectionDetailsSchema),
    defaultValues: transformApiToFormData(initialData),
  });

  const { mutateAsync: updateCongress, isSuccess: isUpdateSuccess } =
    useUpdateCongress(initialData ? initialData.id : "");
  const {
    congress: { id: congressId },
  } = useCongress();

  useEffect(() => {
    if (isUpdateSuccess) {
      queryClient.invalidateQueries({
        queryKey: congressMetaQueryKey(congressId),
      });
    }
  }, [isUpdateSuccess, congressId]);

  const isEdit = !!initialData;

  const handleUpdateCongress = async (data: SectionDetailsFormData) => {
    try {
      const congress = await updateCongress(data);
      form.reset(transformApiToFormData(congress));
    } catch (error) {
      if (error instanceof ApiError) {
        form.setError("root", {
          type: "manual",
          // @ts-expect-error body is not typed
          message: Array.isArray(error.body?.message)
            ? // @ts-expect-error body is not typed
              error.body?.message.join(", ")
            : // @ts-expect-error body is not typed
              error.body?.message,
        });
      }
    }
  };
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleUpdateCongress)}>
        <div className="space-y-6">
          <FormField
            control={form.control}
            name="sectionLeftTemplate"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1">
                  {t("detailsForm.leftColumn")} <MacrosPopover macros={macros} />
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="A-%abstractnum%"
                    className="h-8 px-2 py-1"
                    disabled={form.formState.isSubmitting}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  {t("detailsForm.templateHelp")}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="sectionRightTemplate"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1">
                  {t("detailsForm.rightColumn")} <MacrosPopover macros={macros} />
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="A-%abstractnum%"
                    className="h-8 px-2 py-1"
                    disabled={form.formState.isSubmitting}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  {t("detailsForm.templateHelp")}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid gap-4 lg:grid-cols-2">
            <FormField
              control={form.control}
              name="isSectionRatingEnabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {t("detailsForm.enableRating")}
                    </FormLabel>
                    <FormDescription>{t("detailsForm.sectionsRateable")}</FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isSectionQaEnabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {t("detailsForm.enableQa")}
                    </FormLabel>
                    <FormDescription>
                      {t("detailsForm.sectionsQaEnabled")}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="displayPosterDay"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">{t("detailsForm.posterDay")}</FormLabel>
                    <FormDescription>
                      {t("detailsForm.posterAsSeparateDay")}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="displaySectionGroup"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {t("detailsForm.sectionGroup")}
                    </FormLabel>
                    <FormDescription>
                      {t("detailsForm.sectionGroupVisible")}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          {form.formState.errors.root && (
            <Alert variant="destructive">
              <AlertCircle className="size-4" />
              <AlertTitle>{t("common.errorTitle")}</AlertTitle>
              <AlertDescription>
                {form.formState.errors.root.message}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <div
          className={cn(
            "mt-6 flex sm:justify-between",
            isEdit && "sm:justify-end",
          )}
        >
          {!isEdit && (
            <Button type="button" variant="secondary" onClick={props.onSuccess}>
              {t("congressForm.cancel")}
            </Button>
          )}
          <Button
            className="mb-4 sm:mb-0"
            type="submit"
            disabled={!form.formState.isDirty}
          >
            {isEdit
              ? form.formState.isDirty
                ? t("congressForm.save")
                : t("congressForm.saved")
              : t("congressForm.create")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
